// The AI assistant's chat of a project (#242, spec G11's "History"): its messages as pages, the
// newest 30 first (live's page), each older page asked for when the list is scrolled to its top,
// shown oldest at the top; a message saved, first in the list at once; and live's Reset chat,
// which deletes them all.
import { useInfiniteQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";

import {
  appendAssistMessage,
  getListAssistMessagesQueryKey,
  listAssistMessages,
  useResetAssistMessages,
} from "../../../api/generated/endpoints/editor/editor";
import type { AssistMessage, AssistMessageCreate, AssistMessagePage } from "../../../api/generated/model";

/** Live's page of history. */
export const CHAT_PAGE = 30;

export interface ChatProject {
  teamId: string;
  brandId: string;
  projectId: string;
}

/** The pages as one list, oldest first, each message once (a message may sit on two pages). */
export function oldestFirst(pages: readonly { items: readonly AssistMessage[] }[]): AssistMessage[] {
  const seen = new Set<string>();
  const messages: AssistMessage[] = [];
  for (const page of pages) {
    for (const message of page.items) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      messages.push(message);
    }
  }
  return messages.reverse();
}

export function useAssistChat({ teamId, brandId, projectId }: ChatProject) {
  const queryClient = useQueryClient();
  const queryKey = [...getListAssistMessagesQueryKey(teamId, brandId, projectId), "pages"];
  const pages = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      listAssistMessages(
        teamId,
        brandId,
        projectId,
        { limit: CHAT_PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const reset = useResetAssistMessages({
    mutation: { onSuccess: () => queryClient.resetQueries({ queryKey }) },
  });
  return {
    messages: oldestFirst(pages.data?.pages ?? []),
    loading: pages.isPending,
    error: pages.isError ? pages.error : null,
    hasOlder: pages.hasNextPage,
    loadingOlder: pages.isFetchingNextPage,
    loadOlder: () => {
      if (pages.hasNextPage && !pages.isFetchingNextPage) void pages.fetchNextPage();
    },
    /** Saves a message (live saves the user's, the answer and each step's row) and lists it. */
    save: async (message: AssistMessageCreate): Promise<AssistMessage> => {
      const saved = await appendAssistMessage(teamId, brandId, projectId, message);
      queryClient.setQueryData<InfiniteData<AssistMessagePage, string | undefined>>(queryKey, (data) => {
        const [first, ...older] = data?.pages ?? [];
        if (data === undefined || first === undefined) return data;
        return { ...data, pages: [{ ...first, items: [saved, ...first.items] }, ...older] };
      });
      return saved;
    },
    /** Live's Reset chat: every message of the project gone, for everyone; then `done`. */
    reset: (done: () => void) => reset.mutate({ teamId, brandId, projectId }, { onSuccess: done }),
    resetting: reset.isPending,
    resetError: reset.isError ? reset.error : null,
    /** Forgets the last reset's failure (the dialog opened again). */
    resetForget: () => reset.reset(),
  };
}

export type AssistChat = ReturnType<typeof useAssistChat>;
